import { Box, Card, IconButton, Link, Tooltip, Typography } from "@mui/material";
import { useListContext } from "ra-core";
import { cloneElement, FC } from "react";
import { RecPatch } from "~/dataProvider/tools";
import { BackupJobStatus, DatabaseResponseDto, ValidationJobStatus } from "~/gen/api/backup";
import { FunctionField } from "../field";
import { Datagrid } from "../list/datagrid";
import Scrollbar from "../Scrollbar";
import ArrowRightIcon from '~/icons/ArrowRight';
import { Pagination as DefaultPagination } from '~/components/list/pagination'
import { Children } from "react";
import { useLogViewerContext } from "./LogViewer";
import SizeTextField from "../field/SizeTextField";
import { useBackupListFilterContext } from "./useBackupListFilters";
import { useUserTimeFormat } from "~/utils/userTimeFormat";
import DatabaseNameField from "../databases/DatabaseNameField";
import { EncryptedDatabasesContextProvider } from "../databases/useEncryptedDatabases";


export interface BackupsTableProps {
  onOpenDetail: (database: DatabaseResponseDto | undefined) => void;
}

const TableCommon: FC<BackupsTableProps> = ({
  onOpenDetail,
  children,
}) => {
  const listCtx = useListContext<DatabaseResponseDto & RecPatch>()
  const { AtopFilters } = useBackupListFilterContext()

  return (
    <EncryptedDatabasesContextProvider>

      <Card>
        <AtopFilters />
        <Scrollbar>
          <Box sx={{ minWidth: 1150 }}>

            <Datagrid 
              hasBulkActions={false} 
              sx={{ 
                '& th' : { py: 2 },
                '& td' : { py: 2 },
              }}
              {...listCtx}
            >
              <DatabaseNameField label="Name" />
              <FunctionField 
                source="engineVersion" 
                label="Engine" 
                sortable={false} 
                render={(d?: DatabaseResponseDto & RecPatch) => (
                  <Typography
                    component="p"
                    variant="body2"
                  >
                    {d?.engineVersion + ' ' + (d?.engineEdition || '')}
                  </Typography>
                )}
              />
              <SizeTextField source="sizeText" numericSource="size" label="Size" emptyText="N/A" />
              {Children.map(children, (child: any) => 
                cloneElement(child, [], null))}            
              <FunctionField 
                label="Action"
                sortable={false}
                render={(d?: DatabaseResponseDto & RecPatch) => (
                  <Tooltip title="Detail">
                    <IconButton 
                      sx={{ 
                        color: 'text.primary' as any, 
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                      onClick={() => onOpenDetail(d)}
                    >
                      <ArrowRightIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              />
            </Datagrid>
          </Box>
        </Scrollbar>
        {//@ts-ignore 
        <DefaultPagination {...listCtx} />}
      </Card>
    </EncryptedDatabasesContextProvider>
  )
}

export const BackupsTable: FC<BackupsTableProps> = ({
  onOpenDetail,
}) => {
  const { showLogInViewer } = useLogViewerContext()

  const format = useUserTimeFormat()
  return (
    <TableCommon onOpenDetail={onOpenDetail}>
      <FunctionField 
        source="lastBackupTime"
        label="Last Backup Received"
        render={(d?: DatabaseResponseDto & RecPatch) => (
          <Typography
            component="span"
            variant="body2"
            sx={{
              color: d?.backupJobStatus === BackupJobStatus.Failed ? "error.main" 
                : d?.backupJobStatus === BackupJobStatus.Received ? "success.main" 
                : d?.backupJobStatus === BackupJobStatus.InProgress ? "info.main" 
                : "inherit"
            }}
          >
            {d?.lastBackupTime 
              ? format(d.lastBackupTime, 'Ppp')
              : 'N/A'}
          </Typography>
        )}
      />
      <FunctionField 
        label="Status" 
        sortable={false} 
        render={(d?: DatabaseResponseDto & RecPatch) => (
          <>
            <Typography
              component="p"
              sx={{
                color:
                  d?.backupJobStatus === BackupJobStatus.Failed ? "error.main" 
                  : d?.backupJobStatus === BackupJobStatus.Received ? "success.main" 
                  : d?.backupJobStatus === BackupJobStatus.InProgress ? "info.main" 
                  : "inherit"
              }}
              variant="body2"
            >
              {d?.backupJobStatusText}
            </Typography>
            {d?.backupJobStatus === BackupJobStatus.Failed ?
              <Link
                component="button"
                color="secondary"
                underline="hover"
                onClick={(e:any) => showLogInViewer(e, d.backupFailedLog || '')}
                sx={{ cursor: 'pointer'}}
              >
                <Typography
                  variant="caption"
                >
                  (View Log)
                </Typography>
              </Link> : null
            }
          </>
        )}
      />
    </TableCommon>
  )
}

export const ValidationsTable: FC<BackupsTableProps> = ({
  onOpenDetail,
}) => {
  const { showLogInViewer } = useLogViewerContext()

  const format = useUserTimeFormat()
  return (
    <TableCommon onOpenDetail={onOpenDetail}>
      <FunctionField 
        source="lastValidationTime"
        label="Last Validation"
        render={(d?: DatabaseResponseDto & RecPatch) => (
          <Typography
            component="span"
            variant="body2"
            sx={{
              color:
                d?.validationJobStatus === ValidationJobStatus.Failed ? "error.main" 
                : d?.validationJobStatus === ValidationJobStatus.Validated ? "success.main" 
                : d?.validationJobStatus === ValidationJobStatus.InProgress ? "info.main" 
                : "inherit"
            }}
          >
            {d?.lastValidationTime 
              ? format(d.lastValidationTime, 'Ppp')
              : 'N/A'}
          </Typography>
        )}
      />
      <FunctionField 
        label="Status" 
        sortable={false} 
        render={(d?: DatabaseResponseDto & RecPatch) => (
          <>
            <Typography
              component="p"
              sx={{
                color:
                  d?.validationJobStatus === ValidationJobStatus.Failed ? "error.main" 
                  : d?.validationJobStatus === ValidationJobStatus.Validated ? "success.main" 
                  : d?.validationJobStatus === ValidationJobStatus.InProgress ? "info.main" 
                  : "inherit"
              }}
              variant="body2"
            >
              {d?.validationJobStatusText}
            </Typography>
            {d?.validationJobStatus === ValidationJobStatus.Failed ?
              <Link
                component="button"
                color="secondary"
                underline="hover"
                onClick={(e:any) => showLogInViewer(e, d.validationFailedLog || '')}
                sx={{ cursor: 'pointer'}}
              >
                <Typography
                  variant="caption"
                >
                  (View Log)
                </Typography>
              </Link> : null
            }
          </>
        )}
      />
    </TableCommon>
  )
}
